import { Button, Dialog } from "@material-ui/core";
import { useKiosk } from "application/contexts/kiosk/KioskContext";
import { ErrorCodeType } from "application/models/IError";
import BottomModal from "components/bottomModal/BottomModal";
import { PaymentService } from "domains/order/services/paymentService";
import { useAppInsights } from "Infrastructure/repositories/appInsights/AppInsights";
import { OrderRepositoryLocalStorage } from "Infrastructure/repositories/localStorage/OrderRepositoryLocalStorage";
import { IOrder } from "modules/order/domain/models/IOrder";
import { IOrderLog } from "modules/order/domain/models/IOrderLog";
import {
  MeepPIXEnum,
  PixApprovedTransaction,
  PixKind,
  PixPaymentRequest,
  PixPaymentResponse,
  PixPaymentResponseRaw,
} from "modules/payment/domain/models/PixApprovedTransaction";
import { normalizePixResponse } from "modules/payment/application/pix/pixPayload";
import { isQueueablePix } from "modules/payment/application/pix/pendingPixPolicy";
import {
  IPendingPix,
  markPixActive,
  releasePixActive,
  updatePendingPix,
  upsertPendingPix,
} from "modules/payment/application/pix/pendingPixQueue";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { v4 } from "uuid";
import { createDefferedPromise } from "utils/DefferedPromise";
import { GetError } from "utils/GetError";
import { ReactComponent as Attention } from "../../../../../src/assets/icons/attention.svg";
import styles from "./Pix.module.scss";
import loadQRCode from "../../../../../src/assets/lottie/loadQRCode.gif";
import {
  ResolutionType,
  useScreenStore,
} from "application/contexts/screen/ScreenStore";
import { useTranslation } from "i18n/useTranslation";
import { Trans } from "react-i18next";

const REQUEST_TIMER_DELAY = 5;
const PIX_EXPIRATION_TIMER = 300;
const PIX_PRORROGATION_AUTO_CANCEL = 60;
// Vida do QR na PSP: a tela nunca passa disso desde o POST (tempo do modal conta)
const PIX_MAX_SCREEN_TIME = 600;
const PIX_TIME_OUT_MESSAGE = "Tempo de pagamento pix esgotado";

let requestDelayTimeout: NodeJS.Timeout;
let expirationTimeout: NodeJS.Timeout;
let prorrogationTimeout: NodeJS.Timeout;

const paymentService = PaymentService();
const locaStorageOrderRepository = OrderRepositoryLocalStorage();

export interface IPixHandler {
  open(order: IOrder, kind?: PixKind): Promise<PixApprovedTransaction>;
}

interface IPixProps {}

const Pix: React.ForwardRefRenderFunction<IPixHandler, IPixProps> = (
  _,
  ref
) => {
  const deffered = useRef(createDefferedPromise<PixApprovedTransaction>());

  const { kiosk } = useKiosk();
  const { addLog } = useAppInsights();
  const { resolution } = useScreenStore();
  const { t } = useTranslation();

  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showProrrogationModal, setShowProrrogationModal] = useState(false);
  const [isActiveRequestTimer, setIsActiveRequestTimer] = useState(false);
  const [visible, setVisible] = useState(false);
  const [order, setOrder] = useState<IOrder>({} as IOrder);
  const [expirationCounter, setExpirationCounter] =
    useState(PIX_EXPIRATION_TIMER);
  const [isActiveExpirationTimer, setIsActiveExpirationTimer] = useState(true);
  const [prorrogationCounter, setProrrogationCounter] = useState(
    PIX_PRORROGATION_AUTO_CANCEL
  );
  const [pixStatus, setPixStatus] = useState<PixPaymentResponse | null>(null);
  // Estado (além do ref) para os textos do fluxo enfileirável renderizarem
  const [queueable, setQueueable] = useState(false);
  const [requestTimerDelayCounter, setRequestTimerDelayCounter] =
    useState<number>(REQUEST_TIMER_DELAY);

  // Id do PIX enviado como PixPaymentOrderId (null quando a tela não tem PIX ativo)
  const paymentId = useRef<string | null>(null);
  const pixPaymentStatus = useRef<MeepPIXEnum | null>(null);
  const requestCounter = useRef(0);
  const isQueueable = useRef(false);
  const wasProrrogated = useRef(false);
  // Consulta (GET) só depois que o POST devolveu o PIX (e, se enfileirável, ele já está na fila)
  const isPixCreated = useRef(false);
  // Momento em que o POST devolveu o PIX (limita a prorrogação)
  const pixCreatedAt = useRef<number | null>(null);

  const startExpirationTimer = useCallback(() => {
    wasProrrogated.current = false;
    setShowCancelModal(false);
    setShowProrrogationModal(false);
    setProrrogationCounter(PIX_PRORROGATION_AUTO_CANCEL);
    setExpirationCounter(PIX_EXPIRATION_TIMER);
    setIsActiveExpirationTimer(true);
    setIsActiveRequestTimer(false);
    setRequestTimerDelayCounter(REQUEST_TIMER_DELAY);
  }, []);

  const startPolling = useCallback(() => {
    isPixCreated.current = true;
    pixCreatedAt.current = Date.now();
    setRequestTimerDelayCounter(REQUEST_TIMER_DELAY);
    setIsActiveRequestTimer(true);
  }, []);

  const resetExpirationTimer = useCallback(() => {
    setPixStatus(null);
    clearTimeout(expirationTimeout);
    clearTimeout(prorrogationTimeout);
    setIsActiveExpirationTimer(false);
  }, []);

  const resetRequestTimer = useCallback(() => {
    clearTimeout(requestDelayTimeout);
    setIsActiveRequestTimer(false);
    setRequestTimerDelayCounter(REQUEST_TIMER_DELAY);
  }, []);

  useEffect(() => {
    if (isActiveRequestTimer && requestTimerDelayCounter > 0 && visible) {
      requestDelayTimeout = setTimeout(() => {
        setRequestTimerDelayCounter((prev) => prev - 1);
      }, 1000);
    } else if (isActiveRequestTimer && requestTimerDelayCounter === 0) {
      setRequestTimerDelayCounter(REQUEST_TIMER_DELAY);
    }

    return () => {
      clearTimeout(requestDelayTimeout);
    };
  }, [requestTimerDelayCounter, isActiveRequestTimer, visible]);

  const addOrderLog = useCallback(
    async (
      order: IOrderLog,
      message: string,
      pix: PixPaymentResponse,
      requestCount: number,
      action: string,
      paymentId: string
    ) => {
      try {
        const pixLog = {
          ...pix,
          requestCount,
          action,
          currentPaymentId: paymentId,
          qrCodeBase64: "",
        };
        addLog("pix", { message, order, pixLog });
        await locaStorageOrderRepository.add(
          order,
          message,
          JSON.stringify(pixLog)
        );
      } catch (error) {
        GetError(
          error,
          "addOrderLog",
          "Pix",
          "Falha ao adicionar log",
          ErrorCodeType.GENERAL
        );
      }
    },
    [addLog]
  );

  const closeModals = useCallback(() => {
    setShowCancelModal(false);
    setShowProrrogationModal(false);
  }, []);

  const successPix = useCallback(
    (transaction: PixApprovedTransaction) => {
      setVisible(false);
      closeModals();
      resetExpirationTimer();
      setPixStatus(null);
      pixPaymentStatus.current = null;
      paymentId.current = null;
      deffered.current.resolver(transaction);
    },
    [closeModals, resetExpirationTimer]
  );

  const cancelPixPayment = useCallback(
    async (message?: string) => {
      const currentPixId = paymentId.current;
      // Tela já fechada (aprovado ou cancelado antes)
      if (!currentPixId) return;

      paymentId.current = null;
      pixPaymentStatus.current = null;
      if (isQueueable.current) {
        // O item segue "waiting" na fila para o verificador em segundo plano
        releasePixActive(currentPixId);
      }
      setVisible(false);
      closeModals();
      resetExpirationTimer();
      const cancelledPix = {
        ...pixStatus,
        message: message ?? "Pix - Fechado pelo usuário ou tempo esgotado",
      };
      setPixStatus(null);
      deffered.current?.rejecter(
        GetError(
          message ?? "Pix - Fechado pelo usuário ou tempo esgotado",
          "cancelPixPayment",
          "Pix",
          JSON.stringify(cancelledPix),
          ErrorCodeType.PAYMENT
        )
      );
      await addOrderLog(
        { id: currentPixId } as IOrder,
        "Cancel pix",
        pixStatus ?? ({} as PixPaymentResponse),
        requestCounter.current,
        "CANCELED - Close pix modal",
        currentPixId
      );
      requestCounter.current = 0;
    },
    [pixStatus, closeModals, resetExpirationTimer, addOrderLog]
  );

  const getPixStatus = useCallback(
    async (_paymentId: string) => {
      if (!isPixCreated.current) return;
      try {
        const result = await paymentService.pixPaymentStatus(_paymentId);
        const parseResponse = normalizePixResponse(
          result as PixPaymentResponseRaw
        );
        const currentPixId = paymentId.current;

        if (!parseResponse || parseResponse.paymentId !== currentPixId) {
          // Só registra id diferente enquanto a tela está com PIX ativo
          if (currentPixId) {
            await addOrderLog(
              { id: _paymentId } as IOrder,
              "Pooling",
              parseResponse ?? ({} as PixPaymentResponse),
              requestCounter.current,
              "getPixStatus error: different pix id",
              currentPixId
            );
          }
          return;
        }

        pixPaymentStatus.current = parseResponse.status;

        if (parseResponse.status === "cancelled") {
          resetRequestTimer();
          addOrderLog(
            order,
            "Pix cancelled",
            parseResponse,
            requestCounter.current,
            "Cancelled",
            currentPixId
          );
          cancelPixPayment("QRCode cancelado");
          return;
        }

        if (parseResponse.status === "approved") {
          resetRequestTimer();
          const transactionPix: PixApprovedTransaction = {
            ownerId: parseResponse.ownerId,
            paymentId: parseResponse.paymentId,
            status: parseResponse.status,
            value: parseResponse.value,
            acquirerType: parseResponse.acquirerType,
            approvedAt: parseResponse.approvedAt,
            createdAt: parseResponse.createdAt,
            qrCodeLink: parseResponse.qrCodeLink,
            endToEndId: parseResponse.endToEndId,
          };
          if (isQueueable.current) {
            // O dono só é liberado depois do envio (fluxo de sincronização)
            updatePendingPix(currentPixId, {
              status: "sending",
              pix: transactionPix,
              approvedAt: Date.now(),
              approvedBy: "screen",
            });
          }
          addOrderLog(
            order,
            "Pix Approved",
            parseResponse,
            requestCounter.current,
            "Approved",
            currentPixId
          );
          if (transactionPix.value !== order.totalValue) {
            addOrderLog(
              order,
              `PIX aprovado com valor diferente do pedido: pix ${transactionPix.value}, pedido ${order.totalValue}`,
              parseResponse,
              requestCounter.current,
              "Approved with different value",
              currentPixId
            );
          }
          successPix(transactionPix);
          return;
        }

        setRequestTimerDelayCounter(REQUEST_TIMER_DELAY);
        setPixStatus(parseResponse);
        await addOrderLog(
          { id: _paymentId } as IOrder,
          "Pooling",
          parseResponse,
          requestCounter.current,
          "getPixStatus",
          currentPixId
        );
      } catch (error) {
        pixPaymentStatus.current = "pending";
        GetError(
          error,
          "getPixStatus",
          "Pix",
          undefined,
          ErrorCodeType.PAYMENT
        );
        await addOrderLog(
          { id: _paymentId } as IOrder,
          "Pooling",
          {} as PixPaymentResponse,
          requestCounter.current,
          `getPixStatus error: ${JSON.stringify(error)}`,
          paymentId?.current ?? ""
        );
      } finally {
        requestCounter.current += 1;
      }
    },
    [addOrderLog, cancelPixPayment, order, resetRequestTimer, successPix]
  );

  const onExpirationTimeEnded = useCallback(async () => {
    setIsActiveExpirationTimer(false);
    if (wasProrrogated.current) {
      // Consulta final: se aprovou nesse meio tempo, a aprovação vence
      paymentId.current && (await getPixStatus(paymentId.current));
      cancelPixPayment(PIX_TIME_OUT_MESSAGE);
      return;
    }
    paymentId.current && getPixStatus(paymentId.current);
    setProrrogationCounter(PIX_PRORROGATION_AUTO_CANCEL);
    setShowProrrogationModal(true);
    await addOrderLog(
      { id: paymentId.current } as IOrder,
      "Expiration time",
      {} as PixPaymentResponse,
      requestCounter.current,
      "open expiration modal",
      paymentId?.current ?? ""
    );
  }, [addOrderLog, cancelPixPayment, getPixStatus]);

  useEffect(() => {
    if (!isActiveExpirationTimer || !visible) return;
    if (expirationCounter > 0) {
      expirationTimeout = setTimeout(() => {
        setExpirationCounter((prev) => prev - 1);
      }, 1000);
      return () => {
        clearTimeout(expirationTimeout);
      };
    }
    onExpirationTimeEnded();
  }, [expirationCounter, isActiveExpirationTimer, onExpirationTimeEnded, visible]);

  // Modal de prorrogação sem resposta cancela sozinho
  useEffect(() => {
    if (!showProrrogationModal || !visible) return;
    if (prorrogationCounter > 0) {
      prorrogationTimeout = setTimeout(() => {
        setProrrogationCounter((prev) => prev - 1);
      }, 1000);
      return () => {
        clearTimeout(prorrogationTimeout);
      };
    }
    cancelPixPayment(PIX_TIME_OUT_MESSAGE);
  }, [cancelPixPayment, prorrogationCounter, showProrrogationModal, visible]);

  const onClickProrrogate = useCallback(() => {
    wasProrrogated.current = true;
    setShowProrrogationModal(false);
    setProrrogationCounter(PIX_PRORROGATION_AUTO_CANCEL);
    const elapsed =
      pixCreatedAt.current === null
        ? 0
        : Math.ceil((Date.now() - pixCreatedAt.current) / 1000);
    setExpirationCounter(
      Math.max(0, Math.min(PIX_EXPIRATION_TIMER, PIX_MAX_SCREEN_TIME - elapsed))
    );
    setIsActiveExpirationTimer(true);
    addOrderLog(
      { id: paymentId.current } as IOrder,
      "Prorrogation",
      {} as PixPaymentResponse,
      requestCounter.current,
      "prorrogate expiration time",
      paymentId?.current ?? ""
    );
  }, [addOrderLog]);

  const onClickCancel = useCallback(async () => {
    paymentId.current && getPixStatus(paymentId.current);
    await addOrderLog(
      { id: paymentId.current } as IOrder,
      "Click Cancel Pix",
      {} as PixPaymentResponse,
      requestCounter.current,
      "open cancel modal",
      paymentId?.current ?? ""
    );
    setShowCancelModal(true);
  }, [addOrderLog, getPixStatus]);

  useEffect(() => {
    if (
      isActiveRequestTimer &&
      requestTimerDelayCounter === 0 &&
      paymentId.current &&
      visible
    ) {
      getPixStatus(paymentId.current);
    }
  }, [
    requestTimerDelayCounter,
    isActiveRequestTimer,
    pixStatus,
    visible,
    getPixStatus,
  ]);

  const getPixPayment = useCallback(
    async (
      localClientId: string,
      _order: IOrder,
      pixId: string,
      queueable: boolean,
      kind?: PixKind
    ) => {
      try {
        setPixStatus(null);
        pixPaymentStatus.current = null;
        const payload: PixPaymentRequest = {
          Value: _order.totalValue,
          LocalClientId: localClientId,
          PixPaymentOrderId: pixId,
        };
        const result = await paymentService.pixPayment(payload);
        // Tela fechada ou outro PIX aberto enquanto aguardava o POST
        if (paymentId.current !== pixId) return;

        const parseResponse = normalizePixResponse(
          result as PixPaymentResponseRaw
        );
        if (!parseResponse) {
          await addOrderLog(
            _order,
            "Create Pix",
            {} as PixPaymentResponse,
            requestCounter.current,
            "getPixPayment falha ao obter pix status",
            pixId
          );
          cancelPixPayment("Falha ao obter os dados do pix");
          return;
        }
        pixPaymentStatus.current = parseResponse.status;
        if (queueable) {
          upsertPendingPix({
            pixId,
            kind: kind as IPendingPix["kind"],
            order: _order,
            value: _order.totalValue,
            payer: _order.payer,
            status: "waiting",
            createdAt: Date.now(),
            checkAttempts: 0,
            sendAttempts: 0,
            nfceStatus: "none",
            localId: localClientId,
          });
          markPixActive(pixId);
        }
        startPolling();
        await addOrderLog(
          _order,
          "Create Pix",
          parseResponse,
          requestCounter.current,
          "getPixPayment",
          pixId
        );
        setPixStatus(parseResponse);
      } catch (error) {
        if (paymentId.current !== pixId) return;
        await addOrderLog(
          _order,
          "Create Pix",
          {} as PixPaymentResponse,
          requestCounter.current,
          `getPixPayment error: ${
            (error as any)?.response?.data?.Message ??
            "Falha ao obter os dados do pix"
          }`,
          pixId
        );
        GetError(
          error,
          "getPixPayment",
          "Pix",
          undefined,
          ErrorCodeType.PAYMENT
        );
        cancelPixPayment(
          (error as any)?.response?.data?.message ??
            "Falha ao obter os dados do pix"
        );
      } finally {
        requestCounter.current += 1;
      }
    },
    [addOrderLog, cancelPixPayment, startPolling]
  );

  const open = useCallback(
    (_order: IOrder, kind?: PixKind) => {
      const queueable = isQueueablePix(kind, _order);
      const pixId = queueable ? v4() : _order.id;
      paymentId.current = pixId;
      isQueueable.current = queueable;
      isPixCreated.current = false;
      pixCreatedAt.current = null;
      setQueueable(queueable);
      requestCounter.current = 0;
      pixPaymentStatus.current = null;
      deffered.current = createDefferedPromise<PixApprovedTransaction>();
      setOrder(_order);
      setPixStatus(null);
      kiosk?.localId &&
        getPixPayment(kiosk.localId, _order, pixId, queueable, kind);
      startExpirationTimer();

      setVisible(true);

      addOrderLog(
        _order,
        "Open Pix",
        {} as PixPaymentResponse,
        requestCounter.current,
        "open",
        pixId
      );

      return deffered.current.promise;
    },
    [addOrderLog, getPixPayment, kiosk, startExpirationTimer]
  );

  useImperativeHandle(ref, () => ({ open }));

  const [minuteLeft, minuteRight] = useMemo(
    () =>
      String(Math.floor(expirationCounter / 60))
        .padStart(2, "0")
        .split(""),
    [expirationCounter]
  );
  const [secondLeft, secondRight] = useMemo(
    () =>
      String(expirationCounter % 60)
        .padStart(2, "0")
        .split(""),
    [expirationCounter]
  );

  const textFontSize = resolution === ResolutionType.FULL_HD ? 30 : 22;

  return (
    <Dialog
      id={styles.Pix}
      open={visible}
      data-custom-name="modalPix"
      fullScreen
    >
      <div className={styles.container} data-custom-name="pixContainer">
        <div className={styles.pixBody}>
          {!pixStatus && (
            <div className={styles.loadQRCode}>
              <p style={{ fontSize: textFontSize }}>
                {t("pix.loading.generating_qr")}
              </p>
              <div className={styles.pixQrCode}>
                <img src={loadQRCode} alt="qrCode" />
              </div>
              <p style={{ fontSize: textFontSize }}>
                {t("pix.loading.tip_open_bank_app")}
              </p>
            </div>
          )}

          {pixStatus?.qrCodeBase64 && (
            <div className={styles.pixQrCodeContainer}>
              <p style={{ fontSize: textFontSize }}>
                <Trans i18nKey="pix.qr.intro_line1">
                  Este é o <b>QR Code</b> para pagamento do <b>seu pedido</b>.
                </Trans>
                <br />
                {t("pix.qr.intro_line2")}
              </p>
              {(order.payer?.name || order.payer?.ref) && (
                <div
                  className={styles.payer}
                  data-custom-name="pixPayer"
                  style={{ fontSize: textFontSize }}
                >
                  {order.payer?.name && (
                    <p>{t("pix.qr.payer", { name: order.payer.name })}</p>
                  )}
                  {order.payer?.ref && (
                    <p>{t("pix.qr.reference", { ref: order.payer.ref })}</p>
                  )}
                </div>
              )}
              {order.totalValue && (
                <span>R$ {order.totalValue.toFixed(2).replace(".", ",")}</span>
              )}
              <div className={styles.pixQrCode}>
                <img
                  src={`data:image/jpeg;base64,${pixStatus.qrCodeBase64}`}
                  alt="qrCode"
                />
              </div>
              <div className={styles.countdownContainer}>
                <div>
                  <span>{minuteLeft}</span>
                  <span>{minuteRight}</span>
                </div>
                <span>:</span>
                <div>
                  <span>{secondLeft}</span>
                  <span>{secondRight}</span>
                </div>
              </div>
              <p style={{ fontSize: textFontSize }}>
                <Trans i18nKey="pix.qr.keep_open">
                  Após ler o QR Code, <b>mantenha esta tela aberta</b> até o pagamento ser confirmado.
                </Trans>
                {/* No fluxo enfileirável o pagamento ainda é reconhecido depois de sair */}
                {!queueable && (
                  <>
                    <br />
                    <Trans i18nKey="pix.qr.leave_warning">
                      Se sair agora, <b>seu pagamento não será processado</b>.
                    </Trans>
                  </>
                )}
              </p>
            </div>
          )}
        </div>

        <div className={styles.cancelBotaoGrande} onClick={onClickCancel}>
          {t("pix.actions.cancel")}
        </div>
        <BottomModal
          showModal={showCancelModal}
          onClickClose={() => setShowCancelModal(false)}
        >
          <div className={styles.confirmationModal}>
            <Attention
              style={{
                width: resolution === ResolutionType.FULL_HD ? 200 : 170,
                height: resolution === ResolutionType.FULL_HD ? 200 : 170,
              }}
            />
            <div>
              <br />
              <b
                style={{
                  fontSize: resolution === ResolutionType.FULL_HD ? 60 : 50,
                }}
              >
                {t("pix.cancel_modal.title")}
              </b>
              <br />
              <p style={{ fontSize: textFontSize }}>
                <Trans i18nKey="pix.cancel_modal.line1">
                  Esta tela precisa estar <b>aberta</b> até que seu pagamento via PIX seja <b>confirmado</b>.
                </Trans>
              </p>
              <p style={{ fontSize: textFontSize }}>
                <Trans i18nKey="pix.cancel_modal.line2">
                  Se você sair ou clicar em "Cancelar", o seu pedido não será <b>concluído</b>.
                </Trans>
              </p>
              {queueable && (
                <p style={{ fontSize: textFontSize }}>
                  {t("pix.cancel_modal.line3")}
                </p>
              )}
              <br />
            </div>
          </div>
          <div className={styles.buttonsContainer}>
            <Button
              variant={"outlined"}
              onClick={() => cancelPixPayment()}
              style={{
                backgroundColor: "#FFF",
                color: "var(--primary)",
                border: `2px solid var(--primary)`,
              }}
            >
              {t("pix.actions.cancel")}
            </Button>
            <Button
              variant={"contained"}
              onClick={() => setShowCancelModal(false)}
              style={{
                background: "var(--primary)",
                color: "#FFF",
                border: `3px solid var(--primary)`,
              }}
            >
              {t("pix.actions.back")}
            </Button>
          </div>
        </BottomModal>

        <BottomModal showModal={showProrrogationModal} hideCloseButton>
          <div className={styles.confirmationModal}>
            <Attention
              style={{
                width: resolution === ResolutionType.FULL_HD ? 200 : 170,
                height: resolution === ResolutionType.FULL_HD ? 200 : 170,
              }}
            />
            <div>
              <br />
              <b>{t("pix.extend_modal.title")}</b>
              <br />
              <p style={{ fontSize: textFontSize }}>
                <Trans i18nKey="pix.extend_modal.body">
                  Seu tempo <b>esgotou</b>, deseja prorrogar o tempo?
                </Trans>
              </p>
              <p style={{ fontSize: textFontSize }}>
                {t("pix.extend_modal.auto_cancel", {
                  seconds: prorrogationCounter,
                })}
              </p>
            </div>
          </div>
          <div className={styles.buttonsContainer}>
            <Button
              variant={"outlined"}
              onClick={() => cancelPixPayment()}
              style={{
                backgroundColor: "#FFF",
                color: "var(--primary)",
                border: `2px solid var(--primary)`,
              }}
            >
              {t("pix.actions.cancel")}
            </Button>
            <Button
              variant={"contained"}
              onClick={onClickProrrogate}
              style={{
                background: "var(--primary)",
                color: "#FFF",
                border: `3px solid var(--primary)`,
              }}
            >
              {t("pix.actions.extend")}
            </Button>
          </div>
        </BottomModal>
      </div>
    </Dialog>
  );
};
export default forwardRef(Pix);
